import { useEffect, useRef, useState } from 'react';

import { DatePickerPopover } from 'src/components/DatePickerPopover';

import { useFlags } from 'flagsmith/react';
import { customUseFlags } from 'src/hooks/customUseFlags';
import Animation from 'src/components/common/animation/index.js';
import { useSummarize } from './hooks/useSummarize';
import styles from './summary.module.css';
import { Input } from 'reactstrap';
import { Button } from '@polichat/flamboyant';
import { ResumeAiIcon } from 'src/assets/icons/resume-ai-icon';
import { CalendarIcon } from 'src/assets/icons';

export function SummaryWithDatePicker({ chat }: any) {
  const flag = useFlags(['ia_summary']);
  const ia_summary = customUseFlags(flag.ia_summary);

  const { handleSummarize, isLoading } = useSummarize();

  if (!ia_summary) return null;

  const [show, setShow] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [context, setContext] = useState('');

  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    }

    if (show && showDatePicker === false) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, showDatePicker]);

  function handleClose() {
    setShow(false);
    setContext('');
    setDateRange({ startDate: null, endDate: null });
  }

  function formattedDate(date: Date) {
    return date.toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
    });
  }

  function getFormattedDateRange() {
    if (dateRange.startDate && dateRange.endDate) {
      return (
        formattedDate(dateRange.startDate) +
        ' - ' +
        formattedDate(dateRange.endDate)
      );
    }

    return '';
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        marginLeft: '12px',
      }}
    >
      <Button
        color="secondary"
        onClick={() => setShow((v) => !v)}
        fullWidth
        size="sm"
        style={{
          height: '40px',
          width: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isLoading ? (
          <Animation icon="loading" size="minorLittleTiny" color="#fff" />
        ) : (
          <ResumeAiIcon
            fill="#fff"
            style={{
              height: '20px',
            }}
          />
        )}
      </Button>

      {show && (
        <div className={styles.modal} ref={modalRef}>
          <div>
            <button
              className={styles.btnDatePicker}
              type="button"
              onClick={() => setShowDatePicker(true)}
            >
              <Input
                type="text"
                id="date"
                placeholder="Data"
                value={getFormattedDateRange()}
                className={styles.inputDateRange}
              />

              <CalendarIcon className={styles.calendarIcon} />
            </button>

            <label htmlFor="start_date" className={styles.label}>
              Intervalo máximo de 6 meses. Se não selecionar data final, será
              considerado apenas o dia inicial.
            </label>
          </div>

          <div>
            <Input
              type="text"
              id="summaryContext"
              placeholder=""
              value={context}
              className={styles.inputContext}
              onChange={(event) => {
                setContext(event.target.value);
              }}
            />

            <label htmlFor="summaryContext" className={styles.label}>
              Resumo adaptado ao seu contexto específico
            </label>
          </div>

          <div className={styles.actions}>
            <Button outline color="primary" onClick={handleClose}>
              Cancelar
            </Button>

            <Button
              onClick={() => {
                if (isLoading) {
                  return;
                }

                if (dateRange.startDate && dateRange.endDate) {
                  handleSummarize(
                    chat,
                    {
                      startDate: dateRange.startDate,
                      endDate: dateRange.endDate,
                    },
                    true,
                    [context]
                  );

                  setShow(false);
                }
              }}
            >
              Gerar resumo
            </Button>
          </div>

          {showDatePicker && (
            <DatePickerPopover
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                setShowDatePicker(false);
              }}
              onClear={() => setDateRange({ startDate: null, endDate: null })}
              onClose={() => setShowDatePicker(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}
